import React from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import StorageIcon from '@material-ui/icons/Storage';
import ExtensionIcon from '@material-ui/icons/Extension';
import CloudIcon from '@material-ui/icons/Cloud';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import AssignmentIcon from '@material-ui/icons/Assignment';
import {
  HomePageStarredEntities,
  HomePageRecentlyVisited,
} from '@backstage/plugin-home';
import TextField from '@material-ui/core/TextField';
import {
  GrafanaIcon,
  KeycloakIcon,
  ArgoCDIcon,
  KibanaIcon,
  SonarQubeIcon,
  MinIOIcon,
  PgAdminIcon,
} from '../icons/ToolIcons';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const ORANGE = '#F97316';

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';
  return {
    root: {
      padding: theme.spacing(3),
      maxWidth: 1280,
      margin: '0 auto',
    },
    // ── Hero banner ────────────────────────────────────────────────────────────
    hero: {
      background: theme.palette.background.paper,
      borderRadius: 16,
      padding: theme.spacing(4, 5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(4),
      flexWrap: 'wrap' as const,
      border: '2px solid rgba(249,115,22,0.25)',
      boxShadow: isDark
        ? '0 4px 24px rgba(0,0,0,0.5)'
        : '0 4px 24px rgba(10,37,64,0.07)',
    },
    heroText: {
      flex: 1,
      minWidth: 200,
    },
    heroTitle: {
      color: theme.palette.text.primary,
      fontWeight: 800,
      fontSize: '1.7rem',
      lineHeight: 1.2,
    },
    heroSubtitle: {
      color: ORANGE,
      fontWeight: 500,
      fontSize: '0.9rem',
      marginTop: theme.spacing(0.5),
    },
    heroTagline: {
      color: theme.palette.text.secondary,
      fontSize: '0.82rem',
      marginTop: theme.spacing(0.5),
    },
    // ── Search bar ───────────────────────────────────────────────────────────
    searchWrapper: {
      '& .MuiInputBase-root': {
        borderRadius: 10,
        background: theme.palette.background.paper,
        boxShadow: isDark
          ? '0 2px 10px rgba(0,0,0,0.4)'
          : '0 2px 10px rgba(0,0,0,0.07)',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
      },
      '& .MuiInputBase-input': {
        color: theme.palette.text.primary,
      },
      '& .MuiInputBase-input::placeholder': {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },
    // ── Shortcut cards ────────────────────────────────────────────────────────
    shortcutLabel: {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.08em',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    shortcutCard: {
      height: '100%',
      borderRadius: 12,
      background: theme.palette.background.paper,
      border: `1.5px solid ${theme.palette.divider}`,
      boxShadow: 'none',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 10px 32px rgba(249,115,22,0.15)',
        borderColor: ORANGE,
      },
    },
    shortcutAction: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'flex-start',
      padding: theme.spacing(2.5),
    },
    shortcutIconBox: {
      width: 44,
      height: 44,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(1.5),
      '& svg': { fontSize: '1.35rem' },
    },
    shortcutTitle: {
      fontWeight: 700,
      fontSize: '0.9rem',
      color: theme.palette.text.primary,
      marginBottom: 2,
    },
    shortcutDesc: {
      fontSize: '0.78rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.55,
      whiteSpace: 'pre-line' as const,
    },
    // ── Section labels and cards ──────────────────────────────────────────────
    sectionLabel: {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.08em',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    sectionCard: {
      borderRadius: 12,
      background: theme.palette.background.paper,
      border: `1.5px solid ${theme.palette.divider}`,
      boxShadow: 'none',
      overflow: 'hidden',
    },
    // ── Platform tool cards ───────────────────────────────────────────────────
    toolCard: {
      height: '100%',
      borderRadius: 12,
      background: theme.palette.background.paper,
      border: `1.5px solid ${theme.palette.divider}`,
      boxShadow: 'none',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 8px 24px rgba(249,115,22,0.12)',
        borderColor: ORANGE,
      },
    },
    toolCardInner: {
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(1),
      height: '100%',
    },
    toolHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    toolIconBox: {
      width: 36,
      height: 36,
      borderRadius: 8,
      background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(10,37,64,0.06)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    toolName: {
      fontWeight: 700,
      fontSize: '0.88rem',
      color: theme.palette.text.primary,
    },
    toolDesc: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.5,
    },
    toolLinks: {
      display: 'flex',
      gap: theme.spacing(0.75),
      flexWrap: 'wrap' as const,
      marginTop: 'auto',
      paddingTop: theme.spacing(1),
    },
    toolChipDev: {
      fontSize: '0.68rem',
      height: 22,
      backgroundColor: 'rgba(249,115,22,0.15)',
      color: isDark ? '#FDBA74' : '#C2410C',
      fontWeight: 600,
      cursor: 'pointer',
      '&:hover': { backgroundColor: 'rgba(249,115,22,0.3)' },
    },
  };
});

// ─── Shortcut definitions ─────────────────────────────────────────────────────
interface Shortcut {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
  iconColor: string;
  iconBg: string;
}

const SHORTCUTS: Shortcut[] = [
  {
    icon: <StorageIcon />,
    title: 'Catalogue de Services',
    description: 'Parcourir services,\nsystèmes et équipes',
    path: '/catalog',
    iconColor: '#4a9eff',
    iconBg: 'rgba(74,158,255,0.15)',
  },
  {
    icon: <LibraryBooksIcon />,
    title: 'Confluence',
    description: 'Documentation technique\net wiki d\'équipe',
    path: 'https://boazstudyit.atlassian.net/wiki/home',
    iconColor: ORANGE,
    iconBg: 'rgba(249,115,22,0.15)',
  },
  {
    icon: <AssignmentIcon />,
    title: 'Jira',
    description: 'Gestion des tâches\net suivi des sprints',
    path: 'https://boazstudyit.atlassian.net/jira/for-you',
    iconColor: '#5b8dee',
    iconBg: 'rgba(91,141,238,0.15)',
  },
  {
    icon: <ExtensionIcon />,
    title: 'Explorateur API',
    description: 'Parcourir les specs OpenAPI\net les endpoints',
    path: '/api-docs',
    iconColor: '#42a5f5',
    iconBg: 'rgba(66,165,245,0.15)',
  },
];

// ─── Platform tools ───────────────────────────────────────────────────────────
interface PlatformTool {
  icon: React.ReactNode;
  name: string;
  description: string;
  catalogRef: string;
}

const PLATFORM_TOOLS: PlatformTool[] = [
  {
    icon: <GrafanaIcon fontSize="medium" />,
    name: 'Grafana',
    description: 'Tableaux de bord — métriques Prometheus pour tous les services',
    catalogRef: '/catalog/default/resource/grafana',
  },
  {
    icon: <KeycloakIcon fontSize="medium" />,
    name: 'Keycloak',
    description: 'Gestion des identités et accès — fournisseur OAuth2/OIDC',
    catalogRef: '/catalog/default/resource/keycloak',
  },
  {
    icon: <ArgoCDIcon fontSize="medium" />,
    name: 'ArgoCD',
    description: 'Livraison continue GitOps — gère tous les déploiements K8s',
    catalogRef: '/catalog/default/resource/argocd',
  },
  {
    icon: <KibanaIcon fontSize="medium" />,
    name: 'Kibana',
    description: 'Analyse des logs — stack ELK, centralise les logs de tous les services',
    catalogRef: '/catalog/default/resource/kibana',
  },
  {
    icon: <SonarQubeIcon fontSize="medium" />,
    name: 'SonarQube',
    description: 'Qualité du code — analyse statique et détection de vulnérabilités',
    catalogRef: '/catalog/default/resource/sonarqube',
  },
  {
    icon: <MinIOIcon fontSize="medium" />,
    name: 'MinIO',
    description: 'Stockage objet — PDFs et fichiers pour caisse & documents',
    catalogRef: '/catalog/default/resource/minio',
  },
  {
    icon: <PgAdminIcon fontSize="medium" />,
    name: 'pgAdmin',
    description: 'Interface admin PostgreSQL — gestion des bases de données',
    catalogRef: '/catalog/default/resource/pgadmin',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
export const HomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const textColor = theme.palette.text.primary;

  return (
    <Box className={classes.root}>
      <Grid container spacing={3}>
        {/* ── Hero banner ─────────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Box className={classes.hero}>
            <Box style={{ display: 'flex', alignItems: 'center', flexShrink: 0, height: 56 }}>
              <span style={{ color: '#F97316', fontWeight: 900, fontSize: '2.2rem', lineHeight: 1 }}>B</span>
              <span style={{ color: textColor, fontWeight: 900, fontSize: '1.7rem', lineHeight: 1 }}>oaz</span>
              <span style={{ color: textColor, fontWeight: 400, fontSize: '1.7rem', lineHeight: 1, marginLeft: '0.3em' }}>Study</span>
            </Box>
            <Box className={classes.heroText}>
              <Typography className={classes.heroTitle}>
                Portail Développeur Boaz Study
              </Typography>
              <Typography className={classes.heroSubtitle}>
                La plateforme interne de l'équipe technique
              </Typography>
              <Typography className={classes.heroTagline}>
                Services, documentation, APIs et infrastructure — tout en un.
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* ── Search bar ──────────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Box className={classes.searchWrapper}>
            <TextField
              fullWidth
              variant="outlined"
            placeholder="Rechercher services, docs, APIs, équipes…"
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  const q = (e.target as HTMLInputElement).value.trim();
                  if (q) navigate(`/search?query=${encodeURIComponent(q)}`);
                }
              }}
            />
          </Box>
        </Grid>

        {/* ── Shortcut cards ──────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Typography className={classes.shortcutLabel}>Accès Rapide</Typography>
        </Grid>
        {SHORTCUTS.map(s => (
          <Grid item xs={12} sm={6} md={3} key={s.path}>
            <Card className={classes.shortcutCard}>
              <CardActionArea
                className={classes.shortcutAction}
                onClick={() => {
                  if (s.path.startsWith('http')) {
                    window.open(s.path, '_blank', 'noopener,noreferrer');
                  } else {
                    navigate(s.path);
                  }
                }}
              >
                <Box
                  className={classes.shortcutIconBox}
                  style={{ background: s.iconBg, color: s.iconColor }}
                >
                  {s.icon}
                </Box>
                <Typography className={classes.shortcutTitle}>
                  {s.title}
                </Typography>
                <Typography className={classes.shortcutDesc}>
                  {s.description}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        ))}

        {/* ── Platform Tools ──────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Typography className={classes.sectionLabel}>
            Outils de Plateforme
          </Typography>
        </Grid>
        {PLATFORM_TOOLS.map(tool => (
          <Grid item xs={12} sm={6} md={3} key={tool.name}>
            <Card className={classes.toolCard}>
              <Box className={classes.toolCardInner}>
                <Box className={classes.toolHeader}>
                  <Box className={classes.toolIconBox}>{tool.icon}</Box>
                  <Typography className={classes.toolName}>{tool.name}</Typography>
                </Box>
                <Typography className={classes.toolDesc}>{tool.description}</Typography>
                <Box className={classes.toolLinks}>
                  {tool.catalogRef && (
                    <Chip
                      label="Voir dans le Catalogue"
                      size="small"
                      className={classes.toolChipDev}
                      onClick={() => navigate(tool.catalogRef!)}
                    />
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}

        {/* ── Starred entities ────────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.sectionLabel}>
            ⭐ Entités suivies
          </Typography>
          <Card className={classes.sectionCard}>
            <HomePageStarredEntities />
          </Card>
        </Grid>

        {/* ── Recently visited ────────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.sectionLabel}>
            🕒 Visité récemment
          </Typography>
          <Card className={classes.sectionCard}>
            <HomePageRecentlyVisited />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};